import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, concatMap, delay, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AvailabilityService } from './availability.service';
import { reservationStatus } from '../enums';
import { ShopcartService } from './shopcart.service';
import { LoaderService } from './loader.service';
import { Router } from '@angular/router';
import { SettingsService } from './settings.service';
import { PIMService } from './pim.service';
import { ICartItem, IExtra, IExtraCartItem, IExtraResponseObj, IPaytrailResponseDto, IReservationData } from '../interfaces';
import dayjs from 'dayjs';
import { TranslateService } from '@ngx-translate/core';
import { IReservationFindResponse } from '../interfaces';
import { v4 as uuidv4 } from 'uuid';
import { AnalyticsService } from './analytics.service';
import { LanguageService } from './language.service';
import { StorageService } from './storage.service';
import { paymentError } from '../enums';
import { DebugService } from './debug.service';
import { forEach } from 'lodash';
import { createReservationExtras } from '../reservation-extras';

// the payment gateway's reference type of reservation payments: the PSP lookup answers only with a PSP that takes them
const RESERVATION_PAYMENT_REFERENCE_TYPE = 'Reservation';


@Injectable({
  providedIn: 'root'
})
export class ReservationService {

  private environment: {[key: string]: string};

  resIds:{id: string, email: string}[] = [/* {id: "2657", email: "test@test.fi"}, {id: "2658", email: "test@test.fi"} */]; // reservationIds after reservation, used to find the reservation for confirmation page
  transactionId:string|null // =  '8ca2165e-0885-11f0-9abe-4f706f46d10d';  // if coming from payment, the reservation is fetched with transactionId
  bookerProfileId:number = 0; // 0 jos ei ole tiedossa
  allotments:any
  paymentError: null|paymentError = null;

  constructor(
    private http: HttpClient,
    private avService: AvailabilityService,
    private shopCartService: ShopcartService,
    private loader: LoaderService,
    private router: Router,
    private settings: SettingsService,
    private PIM: PIMService,
    private translateService: TranslateService,
    private analyticsService: AnalyticsService,
    private languageService: LanguageService,
    private storageService: StorageService,
    private debug: DebugService,
  

    @Inject('environment')
    environment: {[key: string]: string}

  ) {
    this.environment = environment;
    this.avService.allotments$.subscribe((res:any) => {

      this.allotments = res;
    });
  }

  getPaymentServiceProvider() {
    let hotelCode:string|undefined = this.avService.getSearchParams()?.hotelCode;
    const paymentEndpoint = this.settings.getPaymentEndpoint();
    if (!paymentEndpoint) { 
      return of(false);
    }

    // response
    //{psp: 'Paytrail'}
    //{psp: 'Viva'}
    //{psp: null} when the hotel takes no reservation payments (a Paytrail configuration for gift cards only does not count)
    let url = `${paymentEndpoint}/Payments/${hotelCode}/psp?referenceType=${RESERVATION_PAYMENT_REFERENCE_TYPE}`;
    return this.http.get(url);
  }

  startVivaPaymentProcess():Observable<any> {

    //clear resIds, so old reservations are not there if booking again
    this.resIds = [];

    this.loader.setLoader('loader.payment');

    let hotelCode:string|undefined = this.avService.getSearchParams()?.hotelCode;
    if (!hotelCode) {
      return of(false);
    }
    let endpoint = this.settings.getEndpoint(hotelCode);
    if (!endpoint) { 
      return of(false);
    }

    let url = `${endpoint}/Reservations/`;
    
    this.bookerProfileId = 0;
    let rooms = this.shopCartService.getCart()?.getCartItemsByType('roomrate');
   
    if (!rooms) {
      return of(false);
    }

    let reservationItems:any[] = this.createReservationItems(rooms, true);

    let body = {
      "Reservations": reservationItems,
      "bookerLanguage": this.languageService.getCurrentLanguage()
    };

    try {
      this.analyticsService.bookingEngineBeginCheckout(reservationItems);
    } catch (error) {
      console.error('Error in analytics bookingEngineBeginCheckout: ', error);
    }
      

    return this.http.post(url, body, {
      responseType: 'json',
      headers: { 'X-Conversation-ID': uuidv4() }
    }).pipe(
      map((res: any) => {
        if (!res.success) {
          throw new Error('Error when creating reservation');
        }
        
        let total = 0;
        let refAmounts: Record<string, string> = {};
    
        res.data.forEach((data: any, index: number) => {
          this.resIds.push({
            id: data.pmsReservationId,
            email: body.Reservations[index].BookerProfile.Emails[0]
          });
          total += parseFloat(body.Reservations[index].RoomRate.rate.mandatoryDepositValue);
          refAmounts[data.pmsReservationId] = body.Reservations[index].RoomRate.rate.mandatoryDepositValue;
        });
    
        // Prepare the payment body
        return {
          paymentBody: {
            Amount: total.toFixed(2),
            Language: this.languageService.getCurrentLanguage()?.toUpperCase(),
            ApplicationRefAmounts: refAmounts,
            CustomerEmail: reservationItems[0].BookerProfile.Emails[0],
            CustomerPhone: reservationItems[0].BookerProfile.MobilePhoneNumber,
            CustomerLastName: reservationItems[0].BookerProfile.LastName,
            CustomerFirstName: reservationItems[0].BookerProfile.FirstName
          }
        };
      }),
      concatMap(({ paymentBody }) => {

        const paymentEndpoint = this.settings.getPaymentEndpoint();
        const paymenturl = `${paymentEndpoint}/payments/viva/${hotelCode}/wbe/`;
        return this.http.post(paymenturl, paymentBody);
      }),
      map((paymentRes: any) => {

        this.loader.hideLoader();
        if (paymentRes.data) {
          
          let paymentOption = 'viva-checkout|viva-redirect'; // viva does not have different payment options, so just use this as default
          let form = this.createPaymentForm(paymentRes.data, paymentOption);
          return form;
        }
        return;
      }),
      catchError((e: any) => {
        this.loader.hideLoader();
        console.error('error', e);
        throw e;
      })
    );
   
  }

  startPaytrailPaymentProcess(paymentOption:string):Observable<any> {

    //clear resIds, so old reservations are not there if booking again
    this.resIds = [];

    this.loader.setLoader('loader.payment');

    let hotelCode:string|undefined = this.avService.getSearchParams()?.hotelCode;
    if (!hotelCode) {
      return of(false);
    }
    let endpoint = this.settings.getEndpoint(hotelCode);
    if (!endpoint) { 
      return of(false);
    }

    let url = `${endpoint}/Reservations/`;
    
    this.bookerProfileId = 0;
    let rooms = this.shopCartService.getCart()?.getCartItemsByType('roomrate');
   
    if (!rooms) {
      return of(false);
    }

    let reservationItems:any[] = this.createReservationItems(rooms, true);

    let body = {
      "Reservations": reservationItems,
      "bookerLanguage": this.languageService.getCurrentLanguage()
    };

    try {
      this.analyticsService.bookingEngineBeginCheckout(reservationItems);
    } catch (error) {
      console.error('Error in analytics bookingEngineBeginCheckout: ', error);
    }
    

    return this.http.post(url, body, {
      responseType: 'json',
      headers: { 'X-Conversation-ID': uuidv4() }
    }).pipe(
      map((res: any) => {
        if (!res.success) {
          throw new Error('Error when creating reservation');
        }
        
        let total = 0;
        let refAmounts: Record<string, string> = {};
    
        res.data.forEach((data: any, index: number) => {
          this.resIds.push({
            id: data.pmsReservationId,
            email: body.Reservations[index].BookerProfile.Emails[0]
          });
          total += parseFloat(body.Reservations[index].RoomRate.rate.mandatoryDepositValue);
          refAmounts[data.pmsReservationId] = body.Reservations[index].RoomRate.rate.mandatoryDepositValue;
        });
    

      

        // Prepare the payment body
        return {
          paymentBody: {
            Amount: total.toFixed(2),
            Language: this.languageService.getCurrentLanguage()?.toUpperCase(),
            ApplicationRefAmounts: refAmounts,
            CustomerEmail: reservationItems[0].BookerProfile.Emails[0],
            CustomerPhone: reservationItems[0].BookerProfile.MobilePhoneNumber,
            CustomerLastName: reservationItems[0].BookerProfile.LastName,
            CustomerFirstName: reservationItems[0].BookerProfile.FirstName
          }
        };
      }),
      concatMap(({ paymentBody }) => {

        const paymentEndpoint = this.settings.getPaymentEndpoint();
        const paymenturl = `${paymentEndpoint}/payments/paytrail/${hotelCode}/wbe/`;
        return this.http.post(paymenturl, paymentBody);
      }),
      map((paymentRes: any) => {

        this.loader.hideLoader();
        if (paymentRes.data && paymentOption) {
          
          let form = this.createPaymentForm(paymentRes.data, paymentOption);

          return form;
        }
        return;
      }),
      catchError((e: any) => {
        this.loader.hideLoader();
        console.error('error', e);
        throw e;
      })
    );
   
  }

  createPaymentForm(allForms:any, SelectedPaymentOption:any):HTMLFormElement|undefined {
    let groupId = SelectedPaymentOption.split("|")[0];
    let buttonId = SelectedPaymentOption.split("|")[1];

    let foundGroup = allForms.groups.find((group:any) => group.id == groupId);
    if (!foundGroup) {
      console.error('Paytrail group not found');
      return;
    }
    let foundButton = foundGroup.buttons.find((button:any) => button.id == buttonId);
    if (!foundButton) {
      console.error('Paytrail button not found');
      return;
    }
    
    // create form from hidden fields.
    const form:HTMLFormElement = document.createElement('form');
    form.method = 'POST';
    form.action = foundButton.redirectUrl;

    // Iterate over each hidden field and add it as an input element to the form
    Object.entries(foundButton.hiddenFields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(value);
      form.appendChild(input);
    });

    return form;
  }

  redirectToConfirmationAfterSuccesfulPayment(querystring:string) {

    let hotelCode = this.shopCartService.getCartHotelId();
    this.loader.setLoader(this.translateService.instant('pr.redirectingToConfirmation'));

    let endpoint = this.settings.getPaymentEndpoint();

    this.http.get(`${endpoint}/Payments/${hotelCode}/success/${querystring}`, { observe: 'response' }).pipe(delay(3000)).subscribe({


      next: (res:any) => {

        if (res.status == 200) {
          this.getTransactionIdFromUrl(querystring).subscribe((id:string|null) => {
            if (id) {
              this.transactionId = id;
              this.debug.log('transaction id saved to service', this.transactionId);
              this.router.navigate(['confirmation']);
            }
            else { 
              this.debug.log('Transaction id not found');
            }
          });          
        }
        else {
          this.debug.log('Error in paymentsuccess', res);

        }
        this.loader.hideLoader();
      },
      error: (err) => {
        this.debug.log('Error in paymentsuccess:', err);
        this.loader.hideLoader();
      }
    });

    
  }

  redirectToProfileAfterFailedPayment(querystring:string) {
    this.loader.setLoader(this.translateService.instant('loader.paymentCancelled'));
    let endpoint = this.settings.getPaymentEndpoint();
    let hotelCode = this.shopCartService.getCartHotelId();
    this.http.get(`${endpoint}/payments/${hotelCode}/cancel/${querystring}`, { observe: 'response' }).subscribe({
      next: (res:any) => {

        if (res.status == 200) {

          this.getTransactionIdFromUrl(querystring).subscribe((id:string|null) => {
            if (id) {
              this.transactionId = id;
              this.debug.log('transaction id saved to service', this.transactionId);
              this.paymentError = paymentError.CANCELLED;
              this.router.navigate(['profile']);
            }
          });          
        }
        else {
          this.debug.log('Error in paymentcancel', res);

        }
        this.loader.hideLoader();
      },
      error: (err) => {
        this.debug.log('Error in paymentcancel:', err);
        this.loader.hideLoader();
      }
    });
    
  }

  getTransactionIdFromUrl(querystring:string) {
    const urlParams = new URLSearchParams(querystring);
    
    return this.getPaymentServiceProvider().pipe(
      map((res: any) => {
        if (res?.psp === 'Paytrail') {
          return urlParams.get('checkout-transaction-id');
        } else if (res?.psp === 'Viva') {
          return urlParams.get('s');
        }
        return null;
      })
    );
  }

  makeReservationWithoutPayment():Observable<any> {

    //clear resIds, so old reservations are not there if booking again
    this.resIds = [];

    this.loader.setLoader('loader.reservation');

    // a reservation that cannot be sent hides the loader and fails, so the caller shows the error
    const cannotReserve = (reason:string) => {
      this.loader.hideLoader();
      console.error(reason);
      return throwError(() => new Error(reason));
    };

    let hotelCode:string|undefined = this.avService.getSearchParams()?.hotelCode;
    if (!hotelCode) {
      return cannotReserve('No hotel code for the reservation');
    }
    let endpoint = this.settings.getEndpoint(hotelCode);
    if (!endpoint) {
      return cannotReserve('No endpoint for the hotel of the reservation');
    }

    let url = `${endpoint}/Reservations/`;

    this.bookerProfileId = 0;
    let rooms:ICartItem[]|undefined = this.shopCartService.getCart()?.getCartItemsByType('roomrate');

    if (!rooms) {
      return cannotReserve('No rooms in the cart for the reservation');
    }

    let reservationItems:any[] = this.createReservationItems(rooms, false);

    let body = {
      "Reservations": reservationItems,
      "bookerLanguage": this.languageService.getCurrentLanguage()
    };

    try {
      this.analyticsService.bookingEngineBeginCheckout(reservationItems);
    } catch (error) {
      console.error('Error in analytics bookingEngineBeginCheckout: ', error);
    }
    

    return this.http.post(url, body, {
      responseType: 'json',
      headers: {
        'X-Conversation-ID': uuidv4()
      }
    }).pipe(
      map((res:any) => {

        if (res.success) {
          res.data.forEach((data:any, index:number) => {
            this.resIds.push({
              id: data.pmsReservationId,
              email: body.Reservations[index].BookerProfile.Emails[0]
            });
          });
          
          this.router.navigate(['confirmation']);
          this.loader.hideLoader();
          try {
            this.analyticsService.bookingEnginePurchase(reservationItems, window.btoa(res.data[0].pmsReservationId));
          } catch (error) {
            console.log('Analytics error', error);
          }
        }
        else {
          // a refused reservation is answered with success: false; the caller shows the error
          console.error('Error when creating reservation', res?.errors);
          throw new Error('Reservation was not created');
        }
      }),
      catchError((e:any) => {
        this.loader.hideLoader();
        console.log('error', e);
        return throwError(() => e);
      })

    );
  }

  createReservationItems(rooms:ICartItem[], isPrepaid: boolean) {

    let resStatus = isPrepaid ? reservationStatus.TEMPORARY : reservationStatus.CONFIRMED;

    let reservationItems:any[] = [];

    let allotment = this.allotments?.find((allotment:any) => {
      return this.avService.getSearchParams()?.bookingCode == allotment.code;
    })

    rooms.forEach((room:ICartItem, index:number) => {

      // if prepaid, but rate does not have mandatory deposit, use total price as deposit.
      let mandatoryDepositValue = room.item.rate.mandatoryDepositValue;
      if (isPrepaid && mandatoryDepositValue < 1) {
        mandatoryDepositValue = room.item.totalPriceForStayWithTaxes;
      }

      // contstruct roomrate to include only values that backend needs
      let roomrate = {
        "room": {
          "roomCode": room.item.room.roomCode
        },
        "rate": {
            "rateCode": room.item.rate.rateCode,
            "cancelPolicyCode": room.item.rate.cancelPolicyCode,
            "mandatoryDepositType": room.item.rate.mandatoryDepositType,
            "mandatoryDepositValue": mandatoryDepositValue.toFixed(2)
        },
        "totalPriceForStayWithTaxes": room.item.totalPriceForStayWithTaxes.toFixed(2),
        "currency": room.item.currency,
        "signature": room.item.signature
      }

      

      let searchMask = room.getSearchParams();
      let roomData = searchMask.roomData[index];    
      let profile = room.profile;

      // each extra with this room's unit price and signature; a prepaid deposit includes them
      let { extras, depositValue } = createReservationExtras(room.extras, index, isPrepaid, roomrate.rate.mandatoryDepositValue);
      roomrate.rate.mandatoryDepositValue = depositValue;
      

      let item:any = {
        "HotelId": searchMask?.hotelCode,
        "Status": resStatus,
        "RoomRate": roomrate,
        "SearchMask": {
          "Arrival": dayjs(searchMask.arrival).format('YYYY-MM-DDTHH:mm:ss'), // send time without timezone
          "Departure": dayjs(searchMask.departure).format('YYYY-MM-DDTHH:mm:ss'),
          "BookingCode": searchMask?.bookingCode,
          "Adults": roomData?.adults,
          "Children": roomData?.children,
          "ChildAges": roomData?.childAges,
          "HotelCode": searchMask?.hotelCode,
          "Rooms": 1
        },
        "BookerProfile": this.createApiProfile(profile),
        "GuestProfiles": [
          this.createApiProfile(profile)
        ],
        "Extras": extras,
        "UserDefinedFields": profile.message ? [
              {
                  "Key": "SpecialRequest",
                  "Value": profile.message
              }
        ] : null,
        "EstTimeOfArrival": profile.eta,
        "TotalPriceForStayWithTaxes": roomrate.totalPriceForStayWithTaxes,
        "TotalPriceForStayWithTaxesAccommodation": room.item.totalPriceForStayWithTaxesAccommodation,
      }
      if (allotment) {
        item["allotmentId"] = allotment.id;
      }

      reservationItems.push(item);
    });
    return reservationItems;
  }

  createApiProfile(profile:any) {
    return {
      "FirstName": profile.firstName,
      "LastName": profile.lastName,
      "IsPrimary": true,
      "Emails": [
        profile.emailAddress
      ],
      "MobilePhoneNumber": profile._phone_full,
      "AddressLine1": profile.address,
      "AddressLine2": "",
      "PostalCode": profile.zipCode,
      "State": "",
      "City": profile.city,
      "CountryISO": profile.country.toUpperCase(),
      "NationalityISO": profile.country.toUpperCase(),
      "LanguageISO": "",
      "AllowMarketing": profile.marketingPermission,
      "CompanyName": profile.companyName?.trim() || null,
      "ExternalLink": profile.externalLink ?? null,
      "Id": profile.id ?? 0
    }
  }

  getNumberOnlyUID():number {
    let random = Math.floor(Math.random() * 99999999);
    return random;
    
    // rajapinnalle liian suuri numero jos lisää timestampin
    /* let d = new Date();
    return d.getTime().toString()+random.toString(); */
  }

  getCurrentReservation():Observable<any> {

    let hotelCode:string|undefined = this.avService.getSearchParams()?.hotelCode;
    if (!hotelCode) {
      return of(false);
    }

    let endpoint = this.settings.getEndpoint(hotelCode);
    
    let conversationId = uuidv4();

    // if transactionId is used, search with it
    if (this.transactionId) {
      
      let url = `${endpoint}/Reservations/payment/${this.transactionId}`;
      
      return  this.http.get<IReservationFindResponse>(`${url}`, {
          responseType: 'json',
          headers: {
            'X-Conversation-ID': conversationId
          }
        })
        .pipe(map((res:any) => {
          let data:any[] = res.data; 
          data.forEach((resData:any) => {

            resData.roomRate.rate['pim'] = this.PIM.getRateData(hotelCode, resData.roomRate.rate.rateCode, resData.reservationAnnotations);
            resData.roomRate.room['pim'] = this.PIM.getRoomData(hotelCode, resData.roomRate.room.roomCode);
            resData.extras?.forEach((extra:IExtra) => {
              extra['pim'] = this.PIM.getExtrasData(hotelCode, extra.rateId);
            });
          });
          try {
            this.analyticsService.bookingEnginePurchase(data, window.btoa(res.data[0].pmsReservationId));
          } catch (error) {
            console.error('Error in analytics bookingEnginePurchase: ', error);
          }
          

          return data;
        }))
      
    }

    // if there is no transactionId the method was PAYMENT_AT_ARRIVAL, search with saved resId and email.
    else {
      let searches:any = [];
      let url = `${endpoint}/Reservations`;
      this.resIds.forEach((resId:{id:string, email:string}) => {

        searches.push(
          this.http.get<IReservationFindResponse>(`${url}/${resId.id}?EmailAddress=${resId.email}`, {
            responseType: 'json',
            headers: {
              'X-Conversation-ID': conversationId
            }
          })
          .pipe(map((res:any) => {
            let data:any = res.data[0]; 
            
            data.roomRate.rate['pim'] = this.PIM.getRateData(hotelCode, data.roomRate.rate.rateCode, data.reservationAnnotations);
            data.roomRate.room['pim'] = this.PIM.getRoomData(hotelCode, data.roomRate.room.roomCode);
            data.extras?.forEach((extra:IExtra) => {
              extra['pim'] = this.PIM.getExtrasData(hotelCode, extra.rateId);
            });
            
            
            return data;
          }))
        );

 
      });

      return forkJoin(searches); 
    }




  }

  findReservation(hotelCode:string, confirmationNumber:string, email:string) {
    
    this.loader.setLoader(this.translateService.instant('fr.searchingReservation'));

    let endpoint = this.settings.getEndpoint(hotelCode);
    let url = `${endpoint}/Reservations`;
    /* let body = {
      "id": confirmationNumber,
      "emailAddress": email
    }  */
    return this.http.get<IReservationFindResponse>(`${url}/${confirmationNumber}?EmailAddress=${email}`, {
      responseType: 'json',
      headers: {
        'X-Conversation-ID': uuidv4()
      }
    })
    .pipe(map(res => {
      let data:IReservationData = res.data[0]; 
      if (res && data.reservationId) {
        data.roomRate.rate['pim'] = this.PIM.getRateData(hotelCode, data.roomRate.rate.rateCode, data.reservationAnnotations);
        data.roomRate.room['pim'] = this.PIM.getRoomData(hotelCode, data.roomRate.room.roomCode);
        data.extras?.forEach((extra:IExtra) => {
          if (extra.rateId) {
            extra['pim'] = this.PIM.getExtrasData(hotelCode, extra.rateId);
          }
        });

      }

      this.loader.hideLoader();
      return data;
    }),
    catchError((e:any, caught: Observable<any>):any => {
      this.loader.hideLoader();
      console.log('error', e);
    }))
 
  }

  cancelReservation(hotelCode:string, reservationData: IReservationData) {
    this.loader.setLoader(this.translateService.instant('fr.cancellingReservation'));

    let endpoint = this.settings.getEndpoint(hotelCode);
    let url = `${endpoint}/Reservations/${reservationData.reservationId}?EmailAddress=${reservationData.bookerProfile.emails[0]}`;
    
    return this.http.delete(url, {
      responseType: 'json',
      headers: {
        'X-Conversation-ID': uuidv4()
      }
    }).pipe(map((res) => {
      this.loader.hideLoader();
      return res;
    }),
    catchError((e:any, caught: Observable<any>):any => {
      this.loader.hideLoader();
      console.log('error', e);
    }))
  }

  // for selected payment provider
  getPaytrailPaymentOptions(amount:number) {
    let language = this.languageService.getCurrentLanguage()?.toUpperCase();
    let hotelCode = this.shopCartService.getCartHotelId();
    const paymentEndpoint = this.settings.getPaymentEndpoint();
    const paymenturl = `${paymentEndpoint}/payments/paytrail/${hotelCode}/providers/grouped`;
    const url = new URL(paymenturl);
    if (amount) {
      url.searchParams.append('Amount', amount.toString());
    }
    if (language) {
      url.searchParams.append('Language', language);
    }
    return this.http.get(url.toString());
  }
}
